import { create } from "zustand";
import { Conversation } from "../api/models/Conversation";
import { Message } from "../api/models/Message";

/** Safely deep clone a conversation object */
function deepClone<T>(obj: T): T {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(obj);
    } catch {
      // Fall back to JSON approach if structuredClone fails
    }
  }
  // Fallback for older browsers or non-clonable objects
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    console.error('Failed to deep clone:', e);
    // Last resort: shallow copy top level, deep copy messages array
    const convo = obj as unknown as Conversation;
    return {
      ...convo,
      messages: convo.messages?.map(m => ({ ...m, content: m.content?.map(c => ({ ...c })) }))
    } as unknown as T;
  }
}

/**
 * Buffer structure that handles multiple "blocks" of streaming content.
 * When sequence numbers reset (indicating a new block), we finalize the current block
 * and start accumulating new content.
 */
interface StreamingBuffer {
  // Accumulated text from completed blocks
  completedText: string;
  // Current block's deltas keyed by sequence number
  currentBlock: { [seq: number]: string };
  // Highest sequence number seen in current block (to detect resets)
  lastMaxSeq: number;
}

function createEmptyBuffer(): StreamingBuffer {
  return { completedText: '', currentBlock: {}, lastMaxSeq: -1 };
}

function applyDeltaToBuffer(buffer: StreamingBuffer, seq: number, delta: string): StreamingBuffer {
  // Detect sequence reset (new block starting)
  // A reset is when we see a sequence number significantly lower than our max
  // We use seq < lastMaxSeq - 1 to allow for some out-of-order delivery
  const isNewBlock = buffer.lastMaxSeq > 0 && seq < buffer.lastMaxSeq - 5;
  
  if (isNewBlock) {
    // Finalize current block and start new one
    const currentBlockText = Object.keys(buffer.currentBlock)
      .map(Number)
      .sort((a, b) => a - b)
      .map((k) => buffer.currentBlock[k])
      .join("");
    
    return {
      completedText: buffer.completedText + currentBlockText + '\n\n',
      currentBlock: { [seq]: delta },
      lastMaxSeq: seq,
    };
  } else {
    // Add to current block
    return {
      completedText: buffer.completedText,
      currentBlock: { ...buffer.currentBlock, [seq]: delta },
      lastMaxSeq: Math.max(buffer.lastMaxSeq, seq),
    };
  }
}

function getBufferText(buffer: StreamingBuffer): string {
  const currentBlockText = Object.keys(buffer.currentBlock)
    .map(Number)
    .sort((a, b) => a - b)
    .map((k) => buffer.currentBlock[k])
    .join("");
  return buffer.completedText + currentBlockText;
}

export interface ConversationState {
  // Core conversation state
  currentConversation: Conversation | undefined;
  conversations: Conversation[];
  messageInProgress: boolean;

  // Streaming delta buffers keyed by conversation ID
  // Uses StreamingBuffer to handle multiple blocks with resetting sequence numbers
  messageDeltaBuffers: { [cid: string]: StreamingBuffer };
  reasoningDeltaBuffers: { [cid: string]: StreamingBuffer };

  // Actions
  setCurrentConversation: (convo: Conversation | undefined) => void;
  setConversations: (convos: Conversation[]) => void;
  setMessageInProgress: (inProgress: boolean) => void;

  /** Apply a streaming message delta for the given conversation. */
  applyMessageDelta: (
    conversationId: string,
    sequenceNumber: number,
    delta: string
  ) => void;

  /** Apply a streaming reasoning delta for the given conversation. */
  applyReasoningDelta: (
    conversationId: string,
    sequenceNumber: number,
    delta: string
  ) => void;

  /** Clear the delta buffers for a conversation after streaming is complete. */
  clearDeltaBuffers: (conversationId: string) => void;

  /** Update the name of a conversation in the list and the current conversation. */
  updateConversationName: (id: string, name: string) => void;

  /** Update the share link of a conversation in the list and the current conversation. */
  updateConversationShareLink: (
    id: string,
    shareLink: string | undefined
  ) => void;

  /** 
   * Merge server conversation with local state, preserving any pending local messages.
   * Use this instead of setCurrentConversation when receiving data from server.
   */
  mergeServerConversation: (serverConvo: Conversation) => void;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  currentConversation: undefined,
  conversations: [],
  messageInProgress: false,
  messageDeltaBuffers: {},
  reasoningDeltaBuffers: {},

  setCurrentConversation: (convo) => set({ currentConversation: convo }),

  setConversations: (convos) => set({ conversations: convos }),

  setMessageInProgress: (inProgress) => set({ messageInProgress: inProgress }),

  applyMessageDelta: (conversationId, sequenceNumber, delta) => {
    const state = get();

    // Update the buffer using the new structure
    const updatedMessageBuffers = { ...state.messageDeltaBuffers };
    const currentBuffer = updatedMessageBuffers[conversationId] || createEmptyBuffer();
    updatedMessageBuffers[conversationId] = applyDeltaToBuffer(currentBuffer, sequenceNumber, delta);

    // Only update the displayed conversation if it's the active one
    const current = state.currentConversation;
    if (!current || current.id !== conversationId || !current.messages) {
      set({ messageDeltaBuffers: updatedMessageBuffers, messageInProgress: true });
      return;
    }

    const deepCopy = deepClone(current);
    const lastMessage = deepCopy.messages![deepCopy.messages!.length - 1];

    const orderedText = getBufferText(updatedMessageBuffers[conversationId]);

    if (!lastMessage || lastMessage.role !== Message.RoleEnum.Assistant) {
      const newMessage: Message = {
        id: "in-flight-message",
        role: Message.RoleEnum.Assistant,
        content: [{ text: orderedText, fileCitations: [], images: [] }],
      };
      deepCopy.messages!.push(newMessage);
    } else {
      if (lastMessage.content && lastMessage.content.length > 0 && lastMessage.content[0]) {
        lastMessage.content[0].text = orderedText;
      }
    }

    set({
      messageDeltaBuffers: updatedMessageBuffers,
      currentConversation: deepCopy,
      messageInProgress: true,
    });
  },

  applyReasoningDelta: (conversationId, sequenceNumber, delta) => {
    const state = get();

    // Update the buffer using the new structure
    const updatedReasoningBuffers = { ...state.reasoningDeltaBuffers };
    const currentBuffer = updatedReasoningBuffers[conversationId] || createEmptyBuffer();
    updatedReasoningBuffers[conversationId] = applyDeltaToBuffer(currentBuffer, sequenceNumber, delta);

    // Only update the displayed conversation if it's the active one
    const current = state.currentConversation;
    if (!current || current.id !== conversationId || !current.messages) {
      set({ reasoningDeltaBuffers: updatedReasoningBuffers, messageInProgress: true });
      return;
    }

    const deepCopy = deepClone(current);
    const lastMessage = deepCopy.messages![deepCopy.messages!.length - 1];

    const orderedText = getBufferText(updatedReasoningBuffers[conversationId]);

    if (!lastMessage || lastMessage.role !== Message.RoleEnum.Assistant) {
      const newMessage: Message = {
        id: "in-flight-message",
        role: Message.RoleEnum.Assistant,
        content: [{ text: "", fileCitations: [], images: [] }],
        thinkingSummary: orderedText,
      };
      deepCopy.messages!.push(newMessage);
    } else {
      lastMessage.thinkingSummary = orderedText;
    }

    set({
      reasoningDeltaBuffers: updatedReasoningBuffers,
      currentConversation: deepCopy,
      messageInProgress: true,
    });
  },

  clearDeltaBuffers: (conversationId) => {
    const state = get();
    const updatedMessageBuffers = { ...state.messageDeltaBuffers };
    const updatedReasoningBuffers = { ...state.reasoningDeltaBuffers };
    delete updatedMessageBuffers[conversationId];
    delete updatedReasoningBuffers[conversationId];
    set({
      messageDeltaBuffers: updatedMessageBuffers,
      reasoningDeltaBuffers: updatedReasoningBuffers,
    });
  },

  updateConversationName: (id, name) => {
    const state = get();
    const updatedConvos = state.conversations.map((c) =>
      c.id === id ? { ...c, name } : c
    );
    const updatedCurrent =
      state.currentConversation?.id === id
        ? { ...state.currentConversation, name }
        : state.currentConversation;
    set({ conversations: updatedConvos, currentConversation: updatedCurrent });
  },

  updateConversationShareLink: (id, shareLink) => {
    const state = get();
    const updatedConvos = state.conversations.map((c) =>
      c.id === id ? { ...c, shareLink } : c
    );
    const updatedCurrent =
      state.currentConversation?.id === id
        ? { ...state.currentConversation, shareLink }
        : state.currentConversation;
    set({ conversations: updatedConvos, currentConversation: updatedCurrent });
  },

  mergeServerConversation: (serverConvo) => {
    const state = get();
    const current = state.currentConversation;

    // If different conversation or no current, just set it
    if (!current || current.id !== serverConvo.id) {
      set({ currentConversation: serverConvo });
      return;
    }

    // Find any pending local messages (temp IDs or in-flight messages)
    const pendingLocalMessages = (current.messages || []).filter(
      (msg) => msg.id?.startsWith('temp-') || msg.id === 'in-flight-message'
    );

    // If no pending local messages, just use server state
    if (pendingLocalMessages.length === 0) {
      set({ currentConversation: serverConvo });
      return;
    }

    // Merge: use server conversation but append pending local messages
    const merged = deepClone(serverConvo);
    merged.messages = merged.messages || [];
    
    // Append pending messages that aren't already on server
    for (const pending of pendingLocalMessages) {
      // Skip in-flight assistant messages - server state is authoritative for those
      if (pending.id === 'in-flight-message') continue;
      
      // Check if this temp message's content already exists on server (by text match)
      const alreadyOnServer = merged.messages.some(
        (serverMsg) =>
          serverMsg.role === pending.role &&
          serverMsg.content?.[0]?.text === pending.content?.[0]?.text
      );
      
      if (!alreadyOnServer) {
        merged.messages.push(pending);
      }
    }

    set({ currentConversation: merged });
  },
}));
